import { useQueryClient } from '@tanstack/react-query'
import { ArrowRightIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button, Checkbox, EmailInput, TextInput, usePageTitle } from 'slab'
import { useGetEmployersEmployersIdOwners, usePostEmployersOwners, usePutEmployersOwnersId } from 'utils/web_api'
import { type EmployersComplianceOwner } from 'utils/web_api.schemas'

import { TrustFooter } from './TrustFooter'
import { CenteredLayout } from '../employer_onboarding/CenteredLayout'

interface OwnerInvitationPageProps {
  employerId: string | null
  employerName?: string
}

export function ControllingOwnerPage ({ employerId }: OwnerInvitationPageProps) {
  usePageTitle('Verify your details')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [controllingOwner, setControllingOwner] = useState<EmployersComplianceOwner | null>(null)
  const [originalData, setOriginalData] = useState<{
    first_name: string
    last_name: string
    title: string
    email: string
    is_beneficial_owner: boolean
    is_controlling_officer: boolean
  } | null>(null)

  // Fetch all owners and filter for controlling owner
  const { data: allOwners, isLoading: isLoadingOwners, error: ownersError } = useGetEmployersEmployersIdOwners(
    employerId || '',
    {
      query: {
        enabled: !!employerId,
      }
    }
  ) as { data: EmployersComplianceOwner[] | null, isLoading: boolean, error: unknown }

  // Update controlling owner state when data is loaded
  useEffect(() => {
    if (allOwners) {
      setControllingOwner(allOwners.find((owner: EmployersComplianceOwner) => owner.is_controlling_officer) || null)
    }
  }, [allOwners])

  const form = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      title: '',
      email: '',
      is_beneficial_owner: false,
      is_controlling_officer: true,
    },
    mode: 'onTouched'
  })

  // Populate form with existing controlling owner data
  useEffect(() => {
    if (controllingOwner) {
      const formData = {
        first_name: controllingOwner.first_name,
        last_name: controllingOwner.last_name,
        title: controllingOwner.title,
        email: controllingOwner.email,
        is_beneficial_owner: controllingOwner.is_beneficial_owner,
        is_controlling_officer: true,
      }

      form.reset(formData)
      setOriginalData(formData)
    }
  }, [controllingOwner, form])

  const createOwnerMutation = usePostEmployersOwners({
    mutation: {
      onSuccess: () => {
        toast.success('Successfully added information!')
      },
      onError: () => {
        toast.error('Error adding individual. If this issue persists, please reach out to us at support@thatch.com')
      }
    }
  })

  const updateOwnerMutation = usePutEmployersOwnersId({
    mutation: {
      onSuccess: () => {
        toast.success('Successfully updated information!')
        queryClient.invalidateQueries({ queryKey: ['getEmployersEmployersIdOwners', employerId] })
      },
      onError: () => {
        toast.error('Error updating individual. If this issue persists, please reach out to us at support@thatch.com')
      }
    }
  })

  const handleBack = () => {
    navigate('/employers/verification/owner_invitation/welcome')
  }

  const hasFormDataChanged = () => {
    if (!originalData) return true // If no original data, consider it changed (new owner)

    const currentData = form.getValues()

    return (
      currentData.first_name !== originalData.first_name ||
      currentData.last_name !== originalData.last_name ||
      currentData.title !== originalData.title ||
      currentData.email !== originalData.email ||
      currentData.is_beneficial_owner !== originalData.is_beneficial_owner ||
      currentData.is_controlling_officer !== originalData.is_controlling_officer
    )
  }

  const handleSubmit = async () => {
    if (!employerId) {
      toast.error('Employer ID is required')
      return
    }

    const data = form.getValues()
    const ownerData = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      is_controlling_officer: true,
      title: data.title,
      is_beneficial_owner: data.is_beneficial_owner,
    }

    if (controllingOwner?.id) {
      // Check if data has changed before making update API call
      if (hasFormDataChanged()) {
        await updateOwnerMutation.mutateAsync({
          id: controllingOwner.id,
          data: ownerData
        })
      }
    } else {
      // Create new controlling owner
      await createOwnerMutation.mutateAsync({
        data: ownerData
      })
    }

    // Check if beneficial owner exists
    const beneficialOwnerAlreadyExists = allOwners?.some(owner => owner.is_beneficial_owner && owner.id !== controllingOwner?.id) || false

    // Only navigate on success
    if (data.is_beneficial_owner || beneficialOwnerAlreadyExists) {
      navigate('/employers/verification/owner_invitation/add-owners')
    } else {
      navigate('/employers/verification/owner_invitation/ownership-information')
    }
  }

  return (
    <CenteredLayout>
      <CenteredLayout.Header
        heading='Add an individual'
        subheading='First, we must identify an individual with significant control over your company. This can be an executive officer (CEO, CFO, President) or anyone with similar responsibilities.'
        align='center'
      />

      {/* Display existing controlling owner if found */}
      {isLoadingOwners
        ? (
          <CenteredLayout.Container>
            <div className='flex justify-center items-center py-8'>
              <div className='text-gray-500'>Loading existing information...</div>
            </div>
          </CenteredLayout.Container>
          )
        : ownersError
          ? (
            <CenteredLayout.Container>
              <div className='flex justify-center items-center py-8'>
                <div className='text-red-500'>Error loading existing information. Please try again.</div>
              </div>
            </CenteredLayout.Container>
            )
          : null}

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <CenteredLayout.Container>
          <div className='space-y-6'>
            {/* Legal Name */}
            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <TextInput
                  {...form.register('first_name', {
                    required: 'First name is required',
                  })}
                  label='First name'
                  placeholder='First name'
                  error={form.formState.errors?.first_name?.message}
                />
                <TextInput
                  {...form.register('last_name', {
                    required: 'Last name is required',
                  })}
                  label='Last name'
                  placeholder='Last name'
                  error={form.formState.errors?.last_name?.message}
                />
              </div>
            </div>

            {/* Title */}
            <TextInput
              {...form.register('title', {
                required: 'Title is required',
              })}
              label='Title'
              placeholder='CEO, Manager, Lead'
              error={form.formState.errors?.title?.message}
            />

            {/* Email */}
            <div className='space-y-2'>
              <EmailInput
                {...form.register('email', {
                  required: 'Email is required',
                })}
                label='Email'
                placeholder='person@example.com'
                error={form.formState.errors?.email?.message}
              />
              <p className='text-xs text-gray-400 mt-2'>
                We will send an email with instructions on how to complete identity verification to this email address.
              </p>
            </div>

            {/* Beneficial Owner Checkbox */}
            <div className='space-y-2'>
              <Checkbox
                {...form.register('is_beneficial_owner', {
                  onChange: (e) => {
                    if (!e.target.checked) {
                      form.setValue('is_beneficial_owner', false)
                    }
                  }
                })}
                label='This individual owns 25% or more of the company'
              />
            </div>
          </div>
        </CenteredLayout.Container>

        <CenteredLayout.Buttons align='center'>
          <Button
            variant='secondary'
            onClick={handleBack}
            type='button'
            disabled={createOwnerMutation.isPending || updateOwnerMutation.isPending}
          >
            Back
          </Button>
          <Button
            variant='primary'
            type='submit'
            rightIcon={ArrowRightIcon}
            disabled={createOwnerMutation.isPending || updateOwnerMutation.isPending || !form.formState.isValid}
          >
            {(createOwnerMutation.isPending || updateOwnerMutation.isPending) ? 'Submitting...' : 'Continue'}
          </Button>
        </CenteredLayout.Buttons>
      </form>
      <TrustFooter />
    </CenteredLayout>
  )
}
